import { createContext, useCallback, useRef, useState, ReactNode } from 'react';

import { external } from 'libs/http/api/external/external';

interface UserCountryContextValue {
  countryCode: string | null;
  loading: boolean;
  error: Error | null;
  fetchCountryCode: () => Promise<void>;
}

export const UserCountryContext = createContext<UserCountryContextValue>({
  countryCode: null,
  loading: false,
  error: null,
  fetchCountryCode: () => Promise.resolve(),
});

interface UserCountryProviderProps {
  children: ReactNode;
}

export const UserCountryProvider = ({ children }: UserCountryProviderProps) => {
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pending = useRef<Promise<void> | null>(null);

  const fetchCountryCode = useCallback(async () => {
    if (countryCode) {
      return;
    }

    if (pending.current) {
      return pending.current;
    }

    setLoading(true);
    setError(null);

    pending.current = (async () => {
      try {
        const code = await external.getCountryCode();
        setCountryCode(code);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);

        setError(err as Error);
      } finally {
        setLoading(false);
        pending.current = null;
      }
    })();

    return pending.current;
  }, [countryCode]);

  return (
    <UserCountryContext.Provider
      value={{
        countryCode,
        loading,
        error,
        fetchCountryCode,
      }}
    >
      {children}
    </UserCountryContext.Provider>
  );
};
